import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Navigation from './components/Navigation';
import MenuPage from './pages/MenuPage';
import BotMenuPage from './pages/BotMenuPage';
import CartPage from './pages/CartPage';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import AboutPage from './pages/AboutPage';
import TrackOrderPage from './pages/TrackOrderPage';
import { getPlatformUser, api } from './api';
import { getDeepLinkTarget, leaveCollectionTarget, opensOnBouquets } from './utils/deepLink';
import { getPlatform, getWebApp, getStartParam, getInitData, getAuthHeader, resetPlatformCache, syncPlatformAttr, canAskWriteAccess, markWriteAccessAsked, requestWriteAccessNative } from './platform';
import NotifyPermissionPopup from './components/NotifyPermissionPopup';
import { HeroIcon } from './components/NavIcons';
import { useAppVersion } from './useAppVersion';
import { buildProductWindows, supplyFirstBatchOnDate, availabilityProfile, horizonDates, firstPlannedAvailable } from './utils/bouquetAvailability';
import { getCatalogHorizon, loadCatalogHorizon } from './catalogHorizon';
import { shopTimezone } from './utils/timezone';
import { fetchAppSettings, FOREVER } from './settings';
import { trackEvent } from './utils/analytics';
import { trackGoal, trackPurchase, trackSubscriptionPurchased, trackCertificatePurchased, trackMergeSuccess, trackMergeFailed } from './utils/metrika';
import { captureYclid, captureTouch } from './utils/attribution';
import CookieBanner from './components/CookieBanner';

// Ключ для localStorage с учётом пользователя
const getCartKey = () => {
  const user = getPlatformUser();
  const id = user?.telegram_id || user?.max_id || 'guest';
  return `stems_cart_v2_${id}`;
};

const getSubCartKey = () => {
  const user = getPlatformUser();
  const id = user?.telegram_id || user?.max_id || 'guest';
  return `stems_sub_cart_v2_${id}`;
};

const loadSubCart = () => {
  try {
    const saved = localStorage.getItem(getSubCartKey());
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.timestamp && Date.now() - parsed.timestamp < 7 * 24 * 60 * 60 * 1000) {
        return parsed.data || null;
      }
    }
  } catch (e) {}
  return null;
};

const saveSubCart = (data) => {
  try {
    if (data) {
      localStorage.setItem(getSubCartKey(), JSON.stringify({ data, timestamp: Date.now() }));
    } else {
      localStorage.removeItem(getSubCartKey());
    }
  } catch (e) {}
};

// Загрузка корзины из localStorage (минимальный формат: kind + id + quantity).
// Старый формат без kind считается стеблями ('product').
const loadCart = () => {
  try {
    const saved = localStorage.getItem(getCartKey());
    if (saved) {
      const parsed = JSON.parse(saved);
      // Проверяем что данные не старше 7 дней
      if (parsed.timestamp && Date.now() - parsed.timestamp < 7 * 24 * 60 * 60 * 1000) {
        const items = parsed.items || [];
        // Миграция legacy: добавляем kind='product' если нет
        // intended_quantity может отсутствовать у старых записей — fallback на quantity
        return items.map(it => ({
          kind: it.kind || 'product',
          product_id: it.product_id,
          bouquet_id: it.bouquet_id,
          quantity: it.quantity,
          intended_quantity: it.intended_quantity != null ? it.intended_quantity : it.quantity,
        }));
      }
    }
  } catch (e) {
    console.log('Error loading cart:', e);
  }
  return [];
};

// Сохранение корзины в localStorage (kind + id + quantity + intended_quantity).
// intended_quantity = что юзер реально хотел (может быть > quantity если позиция
// "сжата" реконсиляцией). Нужен чтобы после перезапуска приложения reconcileCart
// смог восстановить позицию обратно при освобождении supply, а hydration —
// корректно посчитать diff «что раскупили».
const saveCart = (items) => {
  try {
    const minimalItems = items.map(item => {
      const kind = item.kind || (item.bouquet_id ? 'bouquet' : 'product');
      const intended = item.intended_quantity != null ? item.intended_quantity : item.quantity;
      const out = { kind, quantity: item.quantity, intended_quantity: intended };
      if (kind === 'bouquet') {
        out.bouquet_id = item.bouquet_id || item.id;
      } else {
        out.product_id = item.product_id || item.id;
      }
      return out;
    });
    localStorage.setItem(getCartKey(), JSON.stringify({
      items: minimalItems,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.log('Error saving cart:', e);
  }
};

// Сброс формы оформления при перезагрузке страницы
try { localStorage.removeItem('stems_checkout_form'); } catch (e) {}

/**
 * Чистая функция: per-date перерасчёт корзины против окон запаса (текущий+плановые).
 *  - intended_quantity = что юзер хочет (если null → берём quantity).
 *  - заказ уезжает на ОДНУ дату → лимит позиции = max доступности по достижимым датам
 *    с учётом расхода позиций, добавленных РАНЬШЕ (порядок добавления).
 *  - ужимаем количество только если запрошенное не влезает НИ НА ОДНУ дату; иначе
 *    оставляем (конфликт «на одну дату» объясняет попап на чекауте).
 *  - собранные (assembled) экземпляры букета НЕ берут из общего pool (вычитаем их).
 *  - в 0 → reserved_by = { name: позиция, занявшая цветок раньше } (как у стеблей).
 * Возвращает НОВЫЙ массив cart (или старый если изменений нет).
 */
function reconcileCart(cart, products, bouquets) {
  if (!products || !products.length || !cart || !cart.length) return cart;
  // Дожидаемся гидрации: items без price реконсилить нельзя.
  if (cart.some(it => it.price == null)) return cart;
  // Окна и горизонт — по датам магазина: без его пояса (настройки не пришли) не трогаем.
  if (!shopTimezone()) return cart;

  // Окна запаса по каждому продукту (стебли + компоненты букетов).
  const windowsByPid = {};
  products.forEach(p => { windowsByPid[p.id] = buildProductWindows(p); });
  // Горизонт из настроек (у Букетберри 90): иначе пересчёт корзины не увидел бы поставку,
  // которую чекаут уже предлагает. Синхронно — reconcileCart зовут обработчики, кэш тёплый.
  const horizon = getCatalogHorizon();
  const dates = horizonDates(horizon);

  const newQty = cart.map(it => it.intended_quantity != null ? it.intended_quantity : it.quantity);
  const newReserved = cart.map(() => null);
  const newLimit = cart.map(() => null);  // свежий лимит букета (для +/- гейта в карточке/корзине)
  const newProfile = cart.map(() => null); // cart-aware {today, futureDate, futureCapacity} для строки в корзине

  // allocated[pid] — pool, занятый ранее обработанными позициями.
  // Порядок обработки: сначала последняя изменённая (наивысший _seq), затем
  // остальные ПО ЦЕНЕ УБЫВАЮЩЕЙ (дешёвые режутся первыми). Так пользовательское
  // действие «выигрывает», а в каскаде сначала уходит самое дешёвое.
  const allocated = {};
  const dominantForPid = {};
  const processOrder = cart.map((_, i) => i).sort((a, b) => {
    const seqA = cart[a]._seq || 0;
    const seqB = cart[b]._seq || 0;
    if (seqA !== seqB) return seqB - seqA;
    const priceA = cart[a].price || 0;
    const priceB = cart[b].price || 0;
    return priceB - priceA;
  });

  processOrder.forEach(i => {
    const item = cart[i];
    const requested = newQty[i];
    if (requested <= 0) { newQty[i] = Math.max(0, requested); return; }

    const isBouquet = item.kind === 'bouquet';
    const bDef = isBouquet ? bouquets.find(x => x.id === (item.bouquet_id || item.id)) : null;
    const pids = isBouquet
      ? (bDef?.components || []).map(c => c.product_id)
      : [item.product_id || item.id];

    // Если данных по продукту ещё нет (кэш не загружен) — не трогаем позицию.
    if (pids.length === 0 || pids.some(pid => windowsByPid[pid] == null)) {
      return;  // оставляем intended как есть, в allocated не добавляем
    }

    // Лимит с учётом allocated (раньше добавленных позиций):
    //  - букет: профиль по горизонту (ограничен ближайшей доступной датой — как карточка),
    //  - стебель: ёмкость на ближайшей улучшающей дате, кратно шагу.
    let limit = 0;
    if (isBouquet) {
      const profile = availabilityProfile(bDef, windowsByPid, allocated, horizon);
      limit = profile.limit;
      newProfile[i] = profile;  // штампуем cart-aware числа для строки в корзине
    } else {
      const pid = pids[0];
      const step = item.step || 1;
      const win = windowsByPid[pid];
      const today = Math.floor(Math.max(0, supplyFirstBatchOnDate(win, dates[0]) - (allocated[pid] || 0)) / step) * step;
      let future = 0;
      for (let i = 1; i < dates.length; i++) {
        const a = Math.floor(Math.max(0, supplyFirstBatchOnDate(win, dates[i]) - (allocated[pid] || 0)) / step) * step;
        if (a > today) { future = a; break; }  // самая ранняя улучшающая дата
      }
      limit = Math.max(today, future);
    }

    if (isBouquet) newLimit[i] = limit;
    const fixed = Math.min(requested, Math.max(0, limit));
    newQty[i] = fixed;
    if (fixed === 0) {
      for (const pid of pids) {
        if (dominantForPid[pid]) { newReserved[i] = { name: dominantForPid[pid] }; break; }
      }
    }

    // Фиксируем расход pool этой позицией (recipe-часть; собранные не берут pool).
    if (isBouquet) {
      const recipeUnits = Math.max(0, fixed - (bDef.physical_available_today || 0));
      if (recipeUnits > 0) {
        bDef.components.forEach(c => {
          allocated[c.product_id] = (allocated[c.product_id] || 0) + c.quantity * recipeUnits;
          if (!dominantForPid[c.product_id]) dominantForPid[c.product_id] = item.name;
        });
      }
    } else if (fixed > 0) {
      const pid = pids[0];
      allocated[pid] = (allocated[pid] || 0) + fixed;
      if (!dominantForPid[pid]) dominantForPid[pid] = item.name;
    }
  });

  const anyChange = cart.some((item, i) => {
    if (newQty[i] !== item.quantity) return true;
    if (newLimit[i] != null && newLimit[i] !== item.availableQuantity) return true;
    if (newProfile[i] != null) {
      if (newProfile[i].today !== item.effective_today) return true;
      if ((newProfile[i].futureDate || null) !== (item.effective_future_date || null)) return true;
      if ((newProfile[i].futureCapacity || 0) !== (item.effective_future_capacity || 0)) return true;
    }
    const oldR = item.reserved_by ? item.reserved_by.name : null;
    const newR = newReserved[i] ? newReserved[i].name : null;
    return oldR !== newR;
  });
  if (!anyChange) return cart;

  return cart.map((item, i) => {
    const next = { ...item, quantity: newQty[i] };
    // Свежий лимит букета (с учётом окон + корзины) → гейт +/- в карточке/корзине.
    if (newLimit[i] != null) next.availableQuantity = newLimit[i];
    // Cart-aware числа для отображения в строке корзины (синхрон с карточкой в каталоге).
    if (newProfile[i] != null) {
      next.effective_today = newProfile[i].today;
      next.effective_future_date = newProfile[i].futureDate;
      next.effective_future_capacity = newProfile[i].futureCapacity;
    }
    if (newReserved[i]) next.reserved_by = newReserved[i];
    else delete next.reserved_by;
    return next;
  });
}

// Обёртка для роутинга — трекинг заказа рендерится без основного UI
function AppRouter() {
  const trackMatch = window.location.pathname.match(/^\/track\/(.+)$/);
  if (trackMatch) {
    return <TrackOrderPage token={trackMatch[1]} />;
  }
  return <App />;
}

function App() {
  // Старые web_app-кнопки в чатах (до фикса d27391d) шли с ?tg_id=&tg_fn=.
  // Эти URL запечены в уже отправленных сообщениях бота и сейчас открывают
  // мини-апп без подписанной initData → заказы не подгружаются.
  // Закрываем webview сразу, чтобы клик по такой кнопке выглядел как «ничего
  // не открылось». До broadcast в 11:00 — временная мера.
  if (typeof window !== 'undefined') {
    const _params = new URLSearchParams(window.location.search);
    if (_params.has('tg_id') || _params.has('tg_fn')) {
      try { window.Telegram?.WebApp?.close?.(); } catch {}
      try { window.WebApp?.close?.(); } catch {}
    }
  }

  // Проверка версии приложения
  useAppVersion();

  // Маркеры pending-оплаты. Возврат из ЮКассы проходит через цепочку
  // reload'ов вебвью: старый инстанс мигает первым, съедал маркер и показывал
  // попап «в предыдущем приложении», а финальный открывался ни с чем (плюс
  // start_param теряется на окне согласия Telegram). Поэтому маркер при
  // показе НЕ удаляется, а помечается shownAt: ближайшие 3 минуты любой новый
  // инстанс ещё раз откроет «Мои заказы» с попапом, потом маркер испаряется
  // (2026-09-04). Писатели маркеров пишут plain id — читатель понимает оба
  // формата.
  const PENDING_GRACE_MS = 3 * 60 * 1000;
  const readPendingMarker = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      let parsed = null;
      try { parsed = JSON.parse(raw); } catch (e) {}
      if (parsed && typeof parsed === 'object' && parsed.id) {
        if (parsed.shownAt && Date.now() - parsed.shownAt > PENDING_GRACE_MS) {
          localStorage.removeItem(key);
          return null;
        }
        return String(parsed.id);
      }
      return String(raw);
    } catch (e) {
      return null;
    }
  };
  // Дедуп трекинга покупки: paid-попап может показаться повторно (грейс-маркер,
  // перепроверка после cancelled) — выручка в Метрике при этом задваивалась.
  // true = ещё не трекали, событие можно слать.
  const markPaidTrackedOnce = (kindKey, id) => {
    try {
      const key = `stems_tracked_${kindKey}_${id}`;
      if (localStorage.getItem(key)) return false;
      localStorage.setItem(key, String(Date.now()));
      return true;
    } catch (e) {
      return true;
    }
  };

  const markPendingShown = (key, id) => {
    try {
      let shownAt = Date.now();
      try {
        const parsed = JSON.parse(localStorage.getItem(key));
        if (parsed && parsed.shownAt) shownAt = parsed.shownAt; // грейс идёт от первого показа
      } catch (e) {}
      localStorage.setItem(key, JSON.stringify({ id: String(id), shownAt }));
    } catch (e) {}
  };

  // Проверяем параметр startapp для определения начальной страницы
  const getInitialPage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    const paymentParam = urlParams.get('payment');

    // Если возвращаемся с оплаты - сразу открываем страницу заказов
    if (paymentParam === 'success' || paymentParam === 'fail' || paymentParam === 'cancel') {
      console.log('Payment return detected in getInitialPage:', paymentParam);
      return 'orders';
    }

    if (pageParam === 'orders') {
      return 'orders';
    }

    const startParam = getStartParam();
    if (startParam === 'menu') {
      return 'botmenu';
    }
    if (startParam && startParam.startsWith('link_')) {
      return 'profile';
    }
    if (startParam && (startParam.startsWith('payment_') || startParam.startsWith('fail_'))) {
      // Возврат из ЮКассы deep-link'ом — сразу открываем «Мои заказы», чтобы
      // не мигать главной, пока проверяется статус (2026-09-04). На reload
      // вебвью не дёргаемся — как у cert_ ниже.
      try {
        const navType = performance.getEntriesByType('navigation')[0]?.type;
        if (navType === 'reload' || navType === 'back_forward') return 'menu';
      } catch {}
      return 'orders';
    }
    // start_param может потеряться на окне согласия Telegram — тогда нас
    // спасает свежий pending-маркер: тоже стартуем с «Моих заказов».
    if (readPendingMarker('stems_pending_payment') || readPendingMarker('stems_pending_payment_cert')) {
      return 'orders';
    }
    if (startParam && startParam.startsWith('cert_')) {
      // start_param сохраняется в SDK при reload вебвью. Чтобы при reload
      // не открывать снова профиль, смотрим тип навигации:
      // 'navigate' = свежий запуск (через deeplink) → профиль
      // 'reload' = обновление приложения → главная
      try {
        const navType = performance.getEntriesByType('navigation')[0]?.type;
        if (navType === 'reload' || navType === 'back_forward') return 'menu';
      } catch {}
      return 'profile';
    }
    return 'menu';
  };

  const [activePage, setActivePageRaw] = useState(getInitialPage);
  const [ordersKey, setOrdersKey] = useState(0);
  const [profileKey, setProfileKey] = useState(0);

  const [catalogResetKey, setCatalogResetKey] = useState(0);
  // Текущая страница — через ref: setActivePage зовут и хуки со старым замыканием (ревью 30.10)
  const activePageRef = React.useRef(activePage);
  activePageRef.current = activePage;

  const setActivePage = (page) => {
    if (page === 'orders') {
      setOrdersKey(k => k + 1);
    }
    // «Каталог» внизу, когда уже в каталоге: из подборки — в общий каталог (владелец 30.10)
    if (page === 'menu' && activePageRef.current === 'menu' && leaveCollectionTarget()) {
      setCatalogResetKey(k => k + 1);
    }
    setActivePageRaw(page);
  };

  // Ушли из каталога (корзина, заказы, профиль) — подборка закончилась, вернётся в общий каталог
  useEffect(() => {
    if (activePage !== 'menu') leaveCollectionTarget();
  }, [activePage]);

  // «Перейти в корзину» из quick-view карточки: событие вместо прокидывания
  // колбэка через MenuPage/каталоги в каждую карточку.
  useEffect(() => {
    // Событие шлют quick-view стеблей и букетов — корзина должна открыться
    // на вкладке товаров (иначе залипает ранее выбранная «Подписка»).
    // Подписка в корзину уходит другим путём (onAddSubscriptionToCart).
    const goCart = () => {
      setPreferredCartTab('products');
      setActivePageRaw('cart');
    };
    window.addEventListener('app:go-to-cart', goCart);
    return () => window.removeEventListener('app:go-to-cart', goCart);
  }, []);
  const [cart, setCart] = useState(() => loadCart());
  const [cartLoading, setCartLoading] = useState(false);
  const cartHydratedRef = React.useRef(false);
  const reconcileTimerRef = React.useRef(null);
  // Sequence-счётчик «последнего действия» — последняя изменённая позиция получает
  // наивысший приоритет в reconcileCart (её количество держится, остальные режутся).
  const seqRef = React.useRef(0);
  const nextSeq = () => ++seqRef.current;
  // Cached снимки каталога для синхронной реконсиляции внутри addToCart/etc.
  // Refs (а не state) — чтобы избежать каскада re-render'ов и читать актуальное в setCart callback.
  const productsCacheRef = React.useRef([]);
  const bouquetsCacheRef = React.useRef([]);
  const [hydrationRemovedItems, setHydrationRemovedItems] = useState([]);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // Брендовый попап «разрешите боту писать» — ступень открытия мини-аппа
  const [showWriteAccessPopup, setShowWriteAccessPopup] = useState(false);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const [paidOrderId] = useState(null);
  // Подписка: режим каталога и корзина подписки
  // По ссылке на букет (/bouquet/… или b_<id> из бота) или на раздел букетов витрина открывается на букетах
  const [catalogMode, setCatalogMode] = useState(() => (opensOnBouquets(getDeepLinkTarget()) ? 'bouquets' : 'products')); // 'products' | 'bouquets' | 'subscription'
  const [catalogLoading, setCatalogLoading] = useState(true); // сплэш каталога: скрываем плавающую кнопку корзины
  const [subscriptionCart, setSubscriptionCartRaw] = useState(() => loadSubCart());
  // Текущий выбор на каталоге подписок (item + deliveryCount), обновляется при каждом +/-
  const [subSelection, setSubSelection] = useState(null); // { item, deliveryCount }
  const [preferredCartTab, setPreferredCartTab] = useState(null); // 'products' | 'subscription'
  const setSubscriptionCart = (data) => {
    setSubscriptionCartRaw(data);
    saveSubCart(data);
  };

  // Проверяем наличие платформы (Telegram или Max) — с задержкой для Android
  const [isPlatformReady, setIsPlatformReady] = useState(getPlatform() !== 'web');
  const [, setPlatformChecked] = useState(getPlatform() !== 'web');

  // Расширяем приложение на весь экран
  useEffect(() => {
    const webapp = getWebApp();
    if (webapp?.expand) webapp.expand();
    captureYclid();  // поймать рекламную метку Я.Директа из URL (?yclid=...)
    captureTouch();  // откуда пришёл посетитель: наша ссылка, реклама, Инстаграм, поиск (модуль «Источники заказов»)
    trackEvent('catalog_opened');
    // Открывшим по прямой ссылке без Старта бот писать не может — просим
    // разрешение сразу при входе; вторая попытка будет на «Оформить», если
    // здесь согласия не дали. Пауза — чтобы попап не всплывал поверх
    // загружающейся витрины. Стартовавшим не показывается.
    const writeAccessTimer = setTimeout(() => {
      if (canAskWriteAccess('open')) {
        markWriteAccessAsked('open');
        setShowWriteAccessPopup(true);
      }
    }, 1500);
    return () => clearTimeout(writeAccessTimer);
  }, []);

  // Клиент заводится при первом же открытии мини-аппа (решение 2026-08-17):
  // прямая ссылка t.me/<bot>/<app> и max.ru/<bot>?startapp минует и Старт
  // бота, и Профиль с «Оформить» — такие посетители раньше не попадали в базу
  // вовсе (у Стемса накопилось 504). id берётся из подписанной initData; ждём
  // isPlatformReady — SDK грузится асинхронно. Web не заводим: до SMS-входа
  // идентификатора нет. Ошибка не мешает витрине (fire-and-forget).
  const openRegisteredRef = React.useRef(false);
  useEffect(() => {
    if (!isPlatformReady || openRegisteredRef.current) return;
    const platformUser = getPlatformUser();
    if (!platformUser?.telegram_id && !platformUser?.max_id) return;
    openRegisteredRef.current = true;
    api.getOrCreateUser(platformUser).catch(() => {});
  }, [isPlatformReady]);

  // Ссылка на букет из бота (b_<id>): start_param может прийти позже первого
  // рендера — SDK мессенджера грузится асинхронно
  useEffect(() => {
    const check = () => { if (getDeepLinkTarget()?.kind === 'bouquet') setCatalogMode('bouquets'); };
    check();
    const t = setTimeout(check, 1500);
    return () => clearTimeout(t);
  }, [isPlatformReady]);

  // SDK загружается async — проверяем несколько раз с интервалом
  useEffect(() => {
    if (!isPlatformReady) {
      let attempt = 0;
      const maxAttempts = 10;
      const interval = setInterval(() => {
        attempt++;
        resetPlatformCache();
        const platform = syncPlatformAttr();
        if (platform !== 'web') {
          setIsPlatformReady(true);
          setPlatformChecked(true);
          const webapp = getWebApp();
          if (webapp?.expand) webapp.expand();
          clearInterval(interval);
        } else if (attempt >= maxAttempts) {
          setPlatformChecked(true);
          clearInterval(interval);
        }
      }, 300);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // При возврате из фона проверяем initData — iOS/Android могут убить WebView
  useEffect(() => {
    const platform = getPlatform();
    if (platform === 'web') return; // не нужно для веб-версии

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const initData = getInitData();
      if (initData) return; // initData жив, всё ок

      // initData пуст — SDK потерян, нужна перезагрузка
      const reloadKey = 'stems_reload_visibility';
      const lastReload = localStorage.getItem(reloadKey);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        console.log('initData lost after background, reloading...');
        localStorage.setItem(reloadKey, now.toString());
        trackGoal('auth_reload'); // счётчик берёт metrika.js из env, не зашит
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Состояние для показа уведомления (оплата / привязка)
  const [linkToast, setLinkToast] = useState(null); // { type: 'link_success' | 'link_error', message }
  const [paymentToast, setPaymentToast] = useState(null); // { orderId, type: "success" | "pending" }

  // Итог нативного Telegram-инвойса (CheckoutModal/OrdersPage кидают событие
  // из callback'а openInvoice): попап + «Мои заказы» сразу, без возвратов
  // из внешнего браузера и pending-маркеров.
  useEffect(() => {
    const onInvoiceClosed = (e) => {
      const { status, orderId, orderNumber, total, kind } = e.detail || {};
      const isCert = kind === 'cert';
      const isSub = kind === 'sub';
      const displayNumber = orderNumber || orderId;
      // Итог инвойса авторитетен — гасим pending-маркеры redirect-флоу,
      // чтобы грейс-механизм не дублировал попап на последующих reload
      try {
        localStorage.removeItem('stems_pending_payment');
        localStorage.removeItem('stems_pending_payment_cert');
        localStorage.removeItem('stems_pending_subscription');
      } catch (err) {}
      // Трекинг покупки — строго один раз на сущность (попап может повторяться)
      const trackPaidOnce = () => {
        if (!markPaidTrackedOnce(kind || 'order', orderId)) return;
        if (isCert) {
          trackEvent('certificate_paid', { cert_id: orderId });
          trackCertificatePurchased(total, orderId);
        } else if (isSub) {
          trackSubscriptionPurchased('individual', total);
        } else {
          trackEvent('order_paid', { order_id: orderId });
          trackPurchase({ id: orderId, order_number: orderNumber, total, items: [] });
        }
      };
      if (status === 'paid') {
        trackPaidOnce();
        setPaymentToast({ orderId: displayNumber, type: 'success', isCertificate: isCert, isSubscription: isSub, viaInvoice: true });
        setOrdersKey(k => k + 1); // статус изменился — перечитать список
      } else if (status === 'cancelled' || status === 'failed') {
        // Закрытие 3DS-вебвью крестом даёт cancelled, даже если оплата успела
        // пройти — перед грустным попапом перепроверяем статус в БД (боту
        // нужна пара секунд на successful_payment → confirm).
        setTimeout(() => {
          const apiUrl = process.env.REACT_APP_API_URL || '/api';
          const typeQ = isCert ? '?type=cert' : isSub ? '?type=sub' : '';
          fetch(`${apiUrl}/payment/status/${orderId}${typeQ}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => {
              if (data.payment_status === 'paid') {
                trackPaidOnce();
                setPaymentToast({
                  orderId: (isCert ? data.cert_number : data.order_number) || displayNumber,
                  type: 'success', isCertificate: isCert, isSubscription: isSub, viaInvoice: true,
                });
                setOrdersKey(k => k + 1); // оплата всё же прошла — перечитать
              } else {
                // Не оплачено — в списке ничего не изменилось, только попап,
                // без ремаунта списка (он «рябил» при каждом отказе)
                setPaymentToast({ orderId: displayNumber, type: 'pending', isCertificate: isCert, isSubscription: isSub, viaInvoice: true });
              }
            })
            .catch(() => setPaymentToast({ orderId: displayNumber, type: 'pending', isCertificate: isCert, isSubscription: isSub, viaInvoice: true }));
        }, 1500);
      }
      // pending — молча в заказы: подтверждение придёт через бота.
      // setActivePageRaw, а не setActivePage: обёртка инкрементит ordersKey и
      // ремаунтила список с лоадером даже при отказе от оплаты
      setActivePageRaw('orders');
    };
    window.addEventListener('stems:invoice-closed', onInvoiceClosed);
    return () => window.removeEventListener('stems:invoice-closed', onInvoiceClosed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [forceCloseCheckout, setForceCloseCheckout] = useState(0); // Счётчик для закрытия модалки

  // Проверяем параметры URL при загрузке (возврат после оплаты)
  // Используем ref чтобы избежать повторных вызовов
  const paymentCheckedRef = React.useRef(false);
  
  // Функция проверки статуса оплаты
  const checkPaymentStatus = React.useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const orderId = urlParams.get('order');
    
    // Проверяем start_param из платформы (Telegram/Max)
    const startParam = getStartParam();
    let startParamOrderId = null;
    let startParamFail = false;
    let startParamIsCert = false;

    let alreadyHandled = false;
    if (startParam) {
      console.log('Platform start_param:', startParam);
      // Дедуп обработанного start_param. Для payment_/fail_ он глотает ТОЛЬКО
      // reload вебвью (start_param переживает reload — без этого каждый
      // pull-to-refresh заново показывал бы попап). Настоящий повторный возврат
      // из ЮКассы (navigate) обрабатываем всегда: юзер должен снова попасть в
      // «Мои заказы» и увидеть попап; alreadyHandled лишь гасит повторный
      // трекинг покупки и очистку корзины (2026-09-04).
      const handledKey = 'stems_handled_start_param';
      try {
        alreadyHandled = localStorage.getItem(handledKey) === startParam;
        if (startParam.startsWith('payment_') || startParam.startsWith('fail_')) {
          let navType = '';
          try { navType = performance.getEntriesByType('navigation')[0]?.type; } catch {}
          if (alreadyHandled && (navType === 'reload' || navType === 'back_forward')) {
            console.log('start_param already handled (webview reload), skipping:', startParam);
            return false;
          }
          localStorage.setItem(handledKey, startParam);
        } else if (alreadyHandled) {
          console.log('start_param already handled, skipping:', startParam);
          return false;
        }
      } catch (e) {}
      if (startParam.startsWith('payment_sub_')) {
        // Возврат из оплаты подписки. Используем общий /payment/status?type=sub —
        // тот же паттерн что у заказов и сертификатов: бэк дёргает ЮКассу при
        // pending → возвращает 'paid' / 'failed' / 'unknown'. Для юр-лиц
        // (payment_type='invoice') ЮКасса не дёргается, статус берётся из БД.
        const subId = startParam.replace('payment_sub_', '');
        fetch(`${process.env.REACT_APP_API_URL || '/api'}/payment/status/${subId}?type=sub`, { headers: getAuthHeader() })
          .then(res => res.json())
          .then(data => {
            const orderNum = data.order_number || subId;
            if (data.payment_status === 'paid') {
              trackSubscriptionPurchased(data.payment_type === 'invoice' ? 'company' : 'individual', data.total);
              setPaymentToast({ orderId: orderNum, type: 'success', isSubscription: true });
            } else if (data.payment_status === 'unknown') {
              console.log('Subscription payment status unknown (YooKassa silent), no toast');
            } else {
              setPaymentToast({ orderId: orderNum, type: 'pending', isSubscription: true });
            }
          })
          .catch((err) => {
            console.error('Subscription status check failed:', err);
          });
        setActivePage('orders');
        return true;
      } else if (startParam.startsWith('payment_cert_')) {
        // Возврат из оплаты сертификата (standalone GiftCertificate).
        // ВАЖНО: id-пространства Order и GiftCertificate пересекаются — если
        // Order с таким id существует, /payment/status/{id} найдёт его и
        // вернёт order_type='regular'. Поэтому ставим явный флаг
        // startParamIsCert и фронт берёт ветку cert минуя misroute.
        startParamOrderId = startParam.replace('payment_cert_', '');
        startParamIsCert = true;
      } else if (startParam.startsWith('payment_')) {
        startParamOrderId = startParam.replace('payment_', '');
      } else if (startParam.startsWith('fail_')) {
        startParamOrderId = startParam.replace('fail_', '');
        startParamFail = true;
      }
    }
    
    // Проверяем localStorage на pending payment. Cert-маркер имеет приоритет:
    // id-пространства Order и GiftCertificate пересекаются, без маркера
    // /payment/status/{id} нашёл бы Order первым → ложный тост «Заказ №N».
    let pendingOrderId = null;
    let pendingIsCert = false;
    try {
      const pendingCertId = readPendingMarker('stems_pending_payment_cert');
      if (pendingCertId) {
        pendingOrderId = pendingCertId;
        pendingIsCert = true;
        markPendingShown('stems_pending_payment_cert', pendingCertId);
        console.log('Found pending CERT payment:', pendingCertId);
      } else {
        pendingOrderId = readPendingMarker('stems_pending_payment');
        if (pendingOrderId) {
          console.log('Found pending payment in localStorage:', pendingOrderId);
          markPendingShown('stems_pending_payment', pendingOrderId);
        }
      }
    } catch (e) {
      console.log('Error reading pending payment:', e);
    }

    // Очищаем URL параметры
    const cleanUrl = () => {
      const cleanParams = new URLSearchParams(window.location.search);
      cleanParams.delete('payment');
      cleanParams.delete('order');
      cleanParams.delete('p');
      const cleanSearch = cleanParams.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (cleanSearch ? '?' + cleanSearch : ''));
    };

    // Проверяем pending subscription payment. Раньше тут было 2 дублирующих
    // блока, оба слепо показывали success-toast без запроса статуса. Теперь
    // используем общий /payment/status?type=sub — тот же паттерн что у
    // orders/certs: бэк дёргает ЮКассу при pending и возвращает ground truth.
    try {
      const pendingSub = localStorage.getItem('stems_pending_subscription');
      if (pendingSub) {
        const parsed = JSON.parse(pendingSub);
        const subId = parsed.subscription_id;
        localStorage.removeItem('stems_pending_subscription');
        console.log('Found pending subscription payment:', subId);
        setSubscriptionCart(null);
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        fetch(`${apiUrl}/payment/status/${subId}?type=sub`, { headers: getAuthHeader() })
          .then(res => res.json())
          .then(data => {
            const orderNum = data.order_number || parsed.order_number || subId;
            if (data.payment_status === 'paid') {
              trackSubscriptionPurchased(data.payment_type === 'invoice' ? 'company' : 'individual', data.total);
              setPaymentToast({ orderId: orderNum, type: 'success', isSubscription: true });
            } else if (data.payment_status === 'unknown') {
              console.log('Subscription payment status unknown (YooKassa silent), no toast');
            } else {
              setPaymentToast({ orderId: orderNum, type: 'pending', isSubscription: true });
            }
          })
          .catch((err) => {
            console.error('Subscription status check failed:', err);
          });
        setActivePage('orders');
        cleanUrl();
        return true;
      }
    } catch (e) {
      console.log('Error reading pending subscription:', e);
    }

    // Определяем источник данных
    const effectiveOrderId = orderId || startParamOrderId || pendingOrderId;
    const isSuccess = (payment === 'success') || (startParamOrderId && !startParamFail) || pendingOrderId;
    const isFail = (payment === 'fail' || payment === 'cancel') || startParamFail;
    // Объединяем оба сигнала «это cert»: startParam (payment_cert_*) и
    // localStorage cert-маркер. Любой из них → forces ?type=cert + isCert=true.
    const effectiveIsCert = startParamIsCert || pendingIsCert;

    if (isSuccess && effectiveOrderId) {
      console.log('Payment return detected, checking order status:', effectiveOrderId);

      // Проверяем статус заказа в БД. ?type=cert — если возврат из cert-оплаты
      // (id-пространства Order и GiftCertificate пересекаются, без этого
      // флага бэк может вернуть Order с тем же id вместо cert).
      const _statusUrl = `${process.env.REACT_APP_API_URL || '/api'}/payment/status/${effectiveOrderId}${effectiveIsCert ? '?type=cert' : ''}`;
      fetch(_statusUrl, { headers: getAuthHeader() })
        .then(res => res.json())
        .then(data => {
          console.log('Order status from DB:', data);

          // effectiveIsCert (startParam payment_cert_* ИЛИ localStorage
          // cert-маркер) имеет приоритет над data.order_type — id-пространства
          // Order и GiftCertificate пересекаются, без этого флага бэк может
          // вернуть Order с тем же id для cert-возврата.
          const isCert = effectiveIsCert || data.order_type === 'certificate';
          // Для сертификата: свой номер → номер заказа → и только в крайнем
          // случае сырой id. Без средней ступени юзер видел «Сертификат №5449»
          // (внутренний id) вместо «№108», когда строки сертификата ещё нет.
          const displayNumber = isCert
            ? (data.cert_number || data.order_number || effectiveOrderId)
            : (data.order_number || effectiveOrderId);
          if (data.payment_status === 'paid') {
            // Оплачено — для обычного заказа очищаем корзину и трекаем
            // покупку. Для cert корзину не трогаем (там никаких товаров нет).
            // При повторном возврате (alreadyHandled) попап и навигацию
            // показываем, но покупку повторно не трекаем и корзину не трогаем.
            if (alreadyHandled || !markPaidTrackedOnce(isCert ? 'cert' : 'order', effectiveOrderId)) {
              // Повторный показ (грейс-маркер / reload) — попап без ре-трекинга
              console.log('Repeat payment return, toast only (no re-track)');
            } else if (isCert) {
              trackEvent('certificate_paid', { cert_id: parseInt(effectiveOrderId) });
            } else {
              trackEvent('order_paid', { order_id: parseInt(effectiveOrderId) });
              trackPurchase({
                id: parseInt(effectiveOrderId),
                order_number: data.order_number,
                total: data.total,
                items: data.items || [],
              });
              setCart([]);
              try {
                localStorage.removeItem(getCartKey());
              } catch (e) {
                console.log('Error clearing cart:', e);
              }
            }
            setPaymentToast({ orderId: displayNumber, type: "success", isCertificate: isCert });
            setActivePage('orders');
            // Список мог зафетчиться до того, как /payment/status дожал оплату
            // в БД — перечитываем, чтобы бейдж «Оплачен» появился сразу
            setOrdersKey(k => k + 1);
          } else if (data.payment_status === 'unknown') {
            // ЮКасса не ответила после 2 попыток — не врём юзеру «Не оплатили»,
            // просто молча уводим в «Мои заказы». Webhook позже обновит статус.
            console.log('Payment status unknown (YooKassa API silent), no toast');
            setActivePage('orders');
          } else if (data.payment_status === 'expired' || data.payment_status === 'cancelled') {
            // Заказ уже отменён (61 мин без оплаты или руками). Попап не
            // показываем вовсе — оплачивать нечего, статус виден в списке.
            // Маркер гасим немедленно, без трёхминутного грейса: иначе «Вы не
            // оплатили заказ. Мы грустим» преследовало бы юзера при КАЖДОМ
            // входе все эти три минуты (жалоба 05.09).
            try {
              localStorage.removeItem('stems_pending_payment');
              localStorage.removeItem('stems_pending_payment_cert');
            } catch (e) {}
            setActivePage('orders');
            console.log('Order already cancelled/expired, marker cleared, no toast');
          } else if (pendingOrderId && data.payment_status === "pending") {
            setPaymentToast({ orderId: displayNumber, type: "pending", isCertificate: isCert });
            setActivePage("orders");
            console.log('Payment still pending, waiting for webhook');
          } else {
            console.log("Payment not confirmed, status:", data.payment_status);
            setPaymentToast({ orderId: displayNumber, type: "pending", isCertificate: isCert });
            setActivePage('orders');
          }
          cleanUrl();
        })
        .catch(err => {
          console.error('Error checking payment status:', err);
          setActivePage('orders');
          cleanUrl();
        });
      
      return true; // Обработали
    } else if (isFail) {
      console.log('Payment failed or cancelled');
      setActivePage('orders');
      cleanUrl();
      return true;
    }
    
    return false; // Не было платежа
  }, []);

  // Обработка deep link для привязки аккаунтов (startapp=link_TOKEN)
  const linkCheckedRef = React.useRef(false);
  useEffect(() => {
    if (linkCheckedRef.current) return;
    const startParam = getStartParam();
    if (!startParam || !startParam.startsWith('link_')) return;
    linkCheckedRef.current = true;

    const token = startParam.replace('link_', '');
    const user = getPlatformUser();
    const platformId = user?.telegram_id || user?.max_id;
    if (!platformId || !token) return;

    console.log('Account link detected, confirming token:', token.substring(0, 8) + '...');
    api.confirmLinkToken(token, platformId)
      .then(result => {
        console.log('Account link result:', result);
        trackMergeSuccess(user?.telegram_id ? 'to_telegram' : 'to_max');
        setLinkToast({ type: 'link_success', message: result.message });
        setProfileKey(k => k + 1);
        setActivePage('profile');
      })
      .catch(err => {
        console.log('Account link skipped:', err.message);
        trackMergeFailed(err?.message || 'unknown');
        // Не показываем toast для неактуальных токенов (уже использован, истёк, не найден)
      });
  }, []);

  // Автоскрытие linkToast
  useEffect(() => {
    if (linkToast) {
      const timer = setTimeout(() => setLinkToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [linkToast]);

  // Проверка при первой загрузке
  useEffect(() => {
    if (paymentCheckedRef.current) return;

    if (checkPaymentStatus()) {
      paymentCheckedRef.current = true;
    }
  }, [checkPaymentStatus]);
  
  // Проверка при возврате в приложение (из внешнего браузера)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Проверяем есть ли pending payment (cert или обычный заказ)
        const pendingCertId = readPendingMarker('stems_pending_payment_cert');
        const pendingOrderId = readPendingMarker('stems_pending_payment');
        if (pendingCertId || pendingOrderId) {
          console.log("App became visible, checking pending payment:", pendingCertId || pendingOrderId, "isCert:", !!pendingCertId);
          setForceCloseCheckout(prev => prev + 1);
          checkPaymentStatus();
          return;
        }
        // Проверяем есть ли pending subscription payment. Тот же паттерн что у
        // orders/certs: /payment/status?type=sub с YooKassa-fallback внутри.
        try {
          const pendingSub = localStorage.getItem('stems_pending_subscription');
          if (pendingSub) {
            const parsed = JSON.parse(pendingSub);
            const subId = parsed.subscription_id;
            localStorage.removeItem('stems_pending_subscription');
            console.log("App became visible, checking pending subscription:", subId);
            const apiUrl = process.env.REACT_APP_API_URL || '/api';
            fetch(`${apiUrl}/payment/status/${subId}?type=sub`, { headers: getAuthHeader() })
              .then(res => res.json())
              .then(data => {
                const orderNum = data.order_number || parsed.order_number || subId;
                if (data.payment_status === 'paid') {
                  setPaymentToast({ orderId: orderNum, type: 'success', isSubscription: true });
                } else if (data.payment_status === 'unknown') {
                  console.log('Subscription payment status unknown (YooKassa silent), no toast');
                } else {
                  setPaymentToast({ orderId: orderNum, type: 'pending', isSubscription: true });
                }
              })
              .catch((err) => {
                console.error('Subscription status check failed:', err);
              });
            setActivePage('orders');
          }
        } catch (e) {
          console.log('Error checking pending subscription:', e);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkPaymentStatus]);

  // Автоскрытие toast через 3 секунды.
  // ВАЖНО: запускаем таймер ТОЛЬКО когда document.visibilityState === 'visible'.
  // Сценарий: ЮКасса успешно проводит платёж → авто-редирект через tg:// →
  // TG получает deep link и обрабатывает payment-popup в фоне (mini-app
  // webview ещё в background, юзер сидит в Safari). Без проверки visibility
  // таймер срабатывает за 3 сек пока юзер в Safari, popup исчезает, и при
  // возврате юзер ничего не видит.
  useEffect(() => {
    if (!paymentToast) return;
    // Попап досмотрен — гасим маркеры. Грейс в 3 минуты нужен ровно для одного
    // случая: инстанс, показавший попап, умер вместе с окном согласия Telegram,
    // и новый инстанс должен показать его снова. Но пока маркер жив, попап
    // всплывал ПРИ КАЖДОМ открытии приложения — юзер видел «вы оплатили» и
    // «мы грустим» заново на каждом переключении вкладок (жалоба 05.09).
    // Дожили до конца таймера при видимом приложении — значит человек попап
    // увидел, повторять нечего.
    const dismiss = () => {
      setPaymentToast(null);
      try {
        localStorage.removeItem('stems_pending_payment');
        localStorage.removeItem('stems_pending_payment_cert');
        localStorage.removeItem('stems_pending_subscription');
      } catch (e) {}
    };
    const startTimerWhenVisible = () => {
      if (document.visibilityState !== 'visible') return null;
      return setTimeout(dismiss, 3000);
    };
    let timer = startTimerWhenVisible();
    const onVis = () => {
      if (document.visibilityState === 'visible' && !timer) {
        timer = setTimeout(dismiss, 3000);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [paymentToast]);
  // Сохраняем корзину при каждом изменении
  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  // Initial-mount fetch каталога — чтобы кэш реконсилера был готов к первому add.
  // Иначе sync-реконсилер при пустом кэше отдаст cart как есть, и мерцание total'а
  // вернётся до debounced-refetch'а.
  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.getProducts(null, null, 1, 200).then(r => r.items || r).catch(() => []),
      api.getBouquets({ limit: 200 }).then(r => r.items || []).catch(() => []),
    ]).then(([prods, bqs]) => {
      if (!mounted) return;
      productsCacheRef.current = prods;
      bouquetsCacheRef.current = bqs;
    });
    return () => { mounted = false; };
  }, []);

  // Реконсилер корзины с debounce 300мс — тянет свежие остатки, кладёт в кэш, потом
  // применяет к корзине. Параллельно reconcileSync (ниже) вызывается из add/update —
  // он использует тот же кэш и применяет реконсиляцию синхронно, чтобы не было мерцания
  // total'а между «добавил» и «реконсилер уменьшил».
  useEffect(() => {
    if (cart.length === 0) return;
    if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);

    reconcileTimerRef.current = setTimeout(async () => {
      if (cart.some(it => it.price == null)) return;
      const stemIds = cart.filter(it => (it.kind || 'product') === 'product')
        .map(it => it.product_id || it.id).filter(Boolean);
      const bouquetIds = cart.filter(it => it.kind === 'bouquet')
        .map(it => it.bouquet_id || it.id).filter(Boolean);
      const bouquets = await Promise.all(bouquetIds.map(id => api.getBouquet(id).catch(() => null)));
      const validBouquets = bouquets.filter(Boolean);
      const componentPids = new Set();
      validBouquets.forEach(b => b.components?.forEach(c => componentPids.add(c.product_id)));
      const allPids = Array.from(new Set([...stemIds, ...componentPids]));
      if (allPids.length === 0) return;
      const products = await api.getProductsByIds(allPids).catch(() => []);
      if (!products.length) return;
      // Пояс магазина для дат окон — из настроек; без них реконсиляцию не делаем.
      await fetchAppSettings({ maxAgeMs: FOREVER }).catch(() => null);

      // Обновляем кэш — он используется sync-реконсилером в setCart callbacks.
      productsCacheRef.current = products;
      bouquetsCacheRef.current = validBouquets;

      // Применяем реконсиляцию к текущей корзине.
      setCart(prev => reconcileCart(prev, products, validBouquets));
    }, 300);

    return () => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    };
  }, [cart]);

  // Гидрация корзины: подгружаем актуальные данные товаров с сервера.
  // Букеты (kind='bouquet') гидрируются отдельно через api.getBouquet —
  // их нельзя смешивать с /products/by-ids (id может совпасть с id обычного товара).
  useEffect(() => {
    if (cartHydratedRef.current) return;
    if (cart.length === 0) return;
    if (cart[0].name && cart[0].price != null) {
      cartHydratedRef.current = true;
      return;
    }
    cartHydratedRef.current = true;
    setCartLoading(true);

    const stemItems = cart.filter(it => (it.kind || 'product') === 'product');
    const bouquetItems = cart.filter(it => it.kind === 'bouquet');
    const stemIds = stemItems.map(it => it.product_id || it.id).filter(Boolean);
    const bouquetIds = bouquetItems.map(it => it.bouquet_id || it.id).filter(Boolean);

    Promise.all([
      stemIds.length ? api.getProductsByIds(stemIds).catch(() => []) : Promise.resolve([]),
      Promise.all(bouquetIds.map(id => api.getBouquet(id).catch(() => null))),
    ]).then(([products, bouquets]) => {
      const productMap = {};
      products.forEach(p => { productMap[p.id] = p; });
      const bouquetMap = {};
      bouquets.forEach(b => { if (b) bouquetMap[b.id] = b; });
      const toastItems = [];
      setCart(prev => {
        const hydrated = prev.map(item => {
          // === Bouquet item ===
          if (item.kind === 'bouquet') {
            const bid = item.bouquet_id || item.id;
            const b = bouquetMap[bid];
            if (!b || b.is_hidden) {
              if (item.name || b?.name) toastItems.push({ name: item.name || b?.name, available: 0, gone: true });
              return null;
            }
            const max = b.today_available != null ? b.today_available : (b.max_orderable || 0);
            const totalMax = Math.max(max, b.next_better_capacity || 0);
            // intended_quantity = что юзер хотел до перезапуска (если null — fallback на quantity).
            // Сравниваем с supply именно его, иначе пропустим diff для урезанных reconcile'ом позиций.
            const intended = item.intended_quantity != null ? item.intended_quantity : item.quantity;
            if (totalMax === 0) {
              toastItems.push({ name: b.name, available: 0, gone: true });
              return null;
            }
            let qty = Math.min(intended, totalMax);
            if (intended > totalMax) {
              toastItems.push({ name: b.name, available: totalMax, gone: false });
            }
            return {
              ...b,
              kind: 'bouquet',
              id: b.id,
              bouquet_id: b.id,
              quantity: qty,
              intended_quantity: intended,
              price: b.final_price || b.price,
              base_price: b.price,
              availableQuantity: totalMax,
              inStock: max > 0,
            };
          }
          // === Stem item ===
          const pid = item.product_id || item.id;
          const product = productMap[pid];
          if (!product || product.is_hidden) {
            const name = product?.name || item.name;
            if (name) toastItems.push({ name, available: 0, gone: true });
            return null;
          }
          const availableQuantity = product.quantity || 0;
          const futureAvailable = firstPlannedAvailable(product.future_stock);
          const totalAvailable = Math.max(availableQuantity, futureAvailable);
          const step = product.step || 1;
          const inStock = availableQuantity > 0;
          if (totalAvailable === 0) {
            toastItems.push({ name: product.name, available: 0, gone: true });
            return null;
          }
          // intended_quantity — что юзер хотел до перезапуска. quantity мог быть
          // загнан в 0 реконсиляцией (зарезервирован бекетом). Используем intended
          // для diff'а и стартового значения qty.
          const intended = item.intended_quantity != null ? item.intended_quantity : item.quantity;
          let qty = Math.min(intended, totalAvailable);
          if (intended > totalAvailable) {
            toastItems.push({ name: product.name, available: totalAvailable, gone: false });
          }
          // Если intended=0 (был зарезервирован) — оставляем qty=0, реконсиляция
          // решит выводить ли обратно при свободном supply. Бамп до step применяем
          // только когда юзер реально хотел позицию (intended > 0).
          if (intended > 0 && qty < step) qty = step;
          return {
            ...product,
            kind: 'product',
            quantity: qty,
            intended_quantity: intended,
            availableQuantity: totalAvailable,
            currentStock: availableQuantity,
            step,
            inStock,
            is_expired: product.is_expired || false,
          };
        }).filter(Boolean);
        if (toastItems.length > 0) {
          setTimeout(() => setHydrationRemovedItems(toastItems), 300);
        }
        return hydrated;
      });
    })
      .catch(err => console.log('Cart hydration error:', err))
      .finally(() => setCartLoading(false));
  }, [cart]);

  // При каждом переходе в корзину проверяем наличие и количество товаров
  useEffect(() => {
    if (activePage !== 'cart' || cart.length === 0) return;
    const stemItems = cart.filter(it => (it.kind || 'product') === 'product');
    const bouquetItems = cart.filter(it => it.kind === 'bouquet');
    const stemIds = stemItems.map(it => it.product_id || it.id).filter(Boolean);
    const bouquetIds = bouquetItems.map(it => it.bouquet_id || it.id).filter(Boolean);
    Promise.all([
      stemIds.length ? api.getProductsByIds(stemIds).catch(() => []) : Promise.resolve([]),
      Promise.all(bouquetIds.map(id => api.getBouquet(id).catch(() => null))),
    ]).then(([products, bouquets]) => {
      const productMap = {};
      products.forEach(p => { productMap[p.id] = p; });
      const bouquetMap = {};
      bouquets.forEach(b => { if (b) bouquetMap[b.id] = b; });
      const toastItems = [];
      setCart(prev => {
        const updated = prev.map(item => {
          if (item.kind === 'bouquet') {
            const bid = item.bouquet_id || item.id;
            const b = bouquetMap[bid];
            if (!b || b.is_hidden || !b.is_active) {
              const name = item.name || b?.name;
              if (name) toastItems.push({ name, available: 0, gone: true });
              return null;
            }
            const max = b.today_available || 0;
            const nextBetter = b.next_better_capacity || 0;
            const totalMax = Math.max(max, nextBetter);
            if (totalMax === 0) {
              toastItems.push({ name: b.name, available: 0, gone: true });
              return null;
            }
            const intended = item.intended_quantity != null ? item.intended_quantity : item.quantity;
            let qty = Math.min(intended, totalMax);
            if (intended > totalMax) {
              toastItems.push({ name: b.name, available: totalMax, gone: false });
            }
            if (intended > 0 && qty < 1) qty = 1;
            return {
              ...item,
              ...b,
              kind: 'bouquet',
              bouquet_id: b.id,
              id: b.id,
              quantity: qty,
              intended_quantity: intended,
              // ВАЖНО: price = final_price (с сервисным сбором). Без явной строки
              // спред ...b перетирал бы price полем b.price (база без сбора) →
              // корзина теряла букетный сбор (итого = база). base_price = база.
              price: b.final_price || b.price,
              base_price: b.price,
              availableQuantity: totalMax,
              inStock: max > 0,
            };
          }
          const pid = item.product_id || item.id;
          const product = productMap[pid];
          if (!product || product.is_hidden) {
            const name = item.name || product?.name;
            if (name) toastItems.push({ name, available: 0, gone: true });
            return null;
          }
          const currentQty = product.quantity || 0;
          const futureAvailable = firstPlannedAvailable(product.future_stock);
          const totalAvailable = Math.max(currentQty, futureAvailable);
          const step = product.step || 1;

          if (totalAvailable === 0) {
            toastItems.push({ name: product.name, available: 0, gone: true });
            return null;
          }

          const maxQty = Math.floor(totalAvailable / step) * step;
          const intended = item.intended_quantity != null ? item.intended_quantity : item.quantity;
          let qty = Math.min(intended, maxQty);
          if (intended > maxQty) {
            toastItems.push({ name: product.name, available: maxQty, gone: false });
          }

          return {
            ...item,
            ...product,
            kind: 'product',
            quantity: qty,
            intended_quantity: intended,
            availableQuantity: totalAvailable,
            currentStock: currentQty,
            step,
            inStock: currentQty > 0,
            is_expired: product.is_expired || false,
          };
        }).filter(Boolean);
        if (toastItems.length > 0) {
          setTimeout(() => setHydrationRemovedItems(toastItems), 100);
        }
        return updated;
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage]);

  const addToCart = (product) => {
    const availableQuantity = product.quantity || 0;
    const step = product.step || 1;
    const futureAvailable = firstPlannedAvailable(product.future_stock);
    const totalAvailable = Math.max(availableQuantity, futureAvailable);
    const inStock = availableQuantity > 0;

    trackEvent('cart_added', { product_id: product.id, product_name: product.name });

    setCart(prevCart => {
      const matchId = (item) => (item.kind || 'product') === 'product' && (item.id || item.product_id) === product.id;
      const existingItem = prevCart.find(matchId);
      let updated;
      if (existingItem) {
        const curIntended = existingItem.intended_quantity != null ? existingItem.intended_quantity : existingItem.quantity;
        const newIntended = curIntended + step;
        if (newIntended > totalAvailable) return prevCart;
        const seq = nextSeq();
        updated = prevCart.map(item =>
          matchId(item)
            ? { ...item, quantity: item.quantity + step, intended_quantity: newIntended, _seq: seq }
            : item
        );
      } else {
        updated = [...prevCart, { ...product, kind: 'product', quantity: step, intended_quantity: step, availableQuantity: totalAvailable, currentStock: availableQuantity, step, inStock, _seq: nextSeq() }];
      }
      // Sync-реконсилер на закэшированных каталогах — устраняет мерцание total'а.
      return reconcileCart(updated, productsCacheRef.current, bouquetsCacheRef.current);
    });
  };

  // Добавление букета в общую корзину
  const addBouquetToCart = (bouquet) => {
    // effective_limit (cart-aware, per-date) — приоритетнее сырого max_orderable с бэка.
    const maxOrderable = bouquet.effective_limit != null
      ? bouquet.effective_limit
      : (bouquet.max_orderable != null ? bouquet.max_orderable : (bouquet.kind === 'assembled' ? (bouquet.physical_quantity || 0) : 99));
    trackEvent('cart_added', { bouquet_id: bouquet.id, bouquet_name: bouquet.name });
    setCart(prevCart => {
      const matchId = (item) => item.kind === 'bouquet' && (item.bouquet_id || item.id) === bouquet.id;
      const existing = prevCart.find(matchId);
      let updated;
      if (existing) {
        const curIntended = existing.intended_quantity != null ? existing.intended_quantity : existing.quantity;
        const newIntended = curIntended + 1;
        if (newIntended > maxOrderable) return prevCart;
        const seq = nextSeq();
        updated = prevCart.map(it => matchId(it) ? { ...it, quantity: it.quantity + 1, intended_quantity: newIntended, _seq: seq } : it);
      } else {
        updated = [...prevCart, {
          ...bouquet,
          kind: 'bouquet',
          id: bouquet.id,
          bouquet_id: bouquet.id,
          quantity: 1,
          intended_quantity: 1,
          step: 1,
          price: bouquet.final_price || bouquet.price,
          base_price: bouquet.price,
          availableQuantity: maxOrderable,
          inStock: maxOrderable > 0,
          _seq: nextSeq(),
        }];
      }
      return reconcileCart(updated, productsCacheRef.current, bouquetsCacheRef.current);
    });
  };

  const updateQuantity = (productId, delta, newAvailableQuantity = null, newCurrentStock = null, newFutureStock = null) => {
    setCart(prevCart => {
      const mapped = prevCart.map(item => {
        if ((item.kind || 'product') === 'product' && (item.id || item.product_id) === productId) {
          const effectiveAvailable = newAvailableQuantity !== null ? newAvailableQuantity : (item.availableQuantity || 999);
          const newQuantity = item.quantity + delta;
          const curIntended = item.intended_quantity != null ? item.intended_quantity : item.quantity;
          const newIntended = Math.max(0, curIntended + delta);
          if (delta > 0 && newQuantity > effectiveAvailable) {
            return newAvailableQuantity !== null ? { ...item, availableQuantity: effectiveAvailable } : item;
          }
          let updatedItem = { ...item, availableQuantity: effectiveAvailable, intended_quantity: newIntended, _seq: nextSeq() };
          if (newCurrentStock !== null) updatedItem.currentStock = newCurrentStock;
          if (newFutureStock !== null) updatedItem.future_stock = newFutureStock;
          if (newQuantity > 0) return { ...updatedItem, quantity: newQuantity };
          if (newIntended > 0) return { ...updatedItem, quantity: 0 };
          return null;
        }
        return item;
      }).filter(Boolean);
      return reconcileCart(mapped, productsCacheRef.current, bouquetsCacheRef.current);
    });
  };

  // Изменение количества букета в корзине
  const updateBouquetQuantity = (bouquetId, delta) => {
    setCart(prevCart => {
      const mapped = prevCart.map(item => {
        if (item.kind === 'bouquet' && (item.bouquet_id || item.id) === bouquetId) {
          const newQ = item.quantity + delta;
          const max = item.availableQuantity || 99;
          if (delta > 0 && newQ > max) return item;
          const curIntended = item.intended_quantity != null ? item.intended_quantity : item.quantity;
          const newIntended = Math.max(0, curIntended + delta);
          const seq = nextSeq();
          if (newQ > 0) return { ...item, quantity: newQ, intended_quantity: newIntended, _seq: seq };
          if (newIntended > 0) return { ...item, quantity: 0, intended_quantity: newIntended, _seq: seq };
          return null;
        }
        return item;
      }).filter(Boolean);
      return reconcileCart(mapped, productsCacheRef.current, bouquetsCacheRef.current);
    });
  };

  // Полное удаление item из корзины (для X-кнопки на reserved-строке).
  const removeFromCart = (kind, id) => {
    setCart(prev => {
      const filtered = prev.filter(it => {
        if (kind === 'bouquet') return !(it.kind === 'bouquet' && (it.bouquet_id || it.id) === id);
        return !((it.kind || 'product') === 'product' && (it.id || it.product_id) === id);
      });
      return reconcileCart(filtered, productsCacheRef.current, bouquetsCacheRef.current);
    });
  };

  const clearCart = () => {
    setCart([]);
    // Очищаем и из localStorage
    try {
      localStorage.removeItem(getCartKey());
    } catch (e) {
      console.log('Error clearing cart:', e);
    }
  };

  const renderPage = () => {
    switch (activePage) {
      case 'botmenu':
        return <BotMenuPage setActivePage={setActivePage} />;
      case 'menu':
        return (
          <MenuPage
            addToCart={addToCart}
            addBouquetToCart={addBouquetToCart}
            cart={cart}
            updateQuantity={updateQuantity}
            updateBouquetQuantity={updateBouquetQuantity}
            onLoadingChange={setCatalogLoading}
            resetKey={catalogResetKey}
            catalogMode={catalogMode}
            onCatalogModeChange={(mode) => { setCatalogMode(mode); if (mode !== 'subscription') setSubSelection(null); }}
            onAddSubscriptionToCart={(item, deliveryCount) => {
              setSubscriptionCart({ catalog_item: item, delivery_count: deliveryCount });
              setSubSelection(null);
              setPreferredCartTab('subscription');
              setActivePage('cart');
            }}
            subscriptionCart={subscriptionCart}
            onSubSelectionChange={(item, count) => {
              if (item) {
                setSubSelection({ item, deliveryCount: count });
              } else {
                setSubSelection(null);
                setSubscriptionCart(null);
              }
            }}
            goToOrders={() => setActivePage('orders')}
          />
        );
      case 'cart':
        return (
          <CartPage
            cart={cart}
            cartLoading={cartLoading}
            updateQuantity={updateQuantity}
            updateBouquetQuantity={updateBouquetQuantity}
            removeFromCart={removeFromCart}
            goToMenu={() => setActivePage('menu')}
            goToOrders={() => setActivePage('orders')}
            clearCart={clearCart}
            paymentSuccess={paymentSuccess}
            paymentCancelled={paymentCancelled}
            paidOrderId={paidOrderId}
            resetPaymentSuccess={() => setPaymentSuccess(false)}
            resetPaymentCancelled={() => setPaymentCancelled(false)}
            forceCloseCheckout={forceCloseCheckout}
            hydrationRemovedItems={hydrationRemovedItems}
            onHydrationRemovedSeen={() => setHydrationRemovedItems([])}
            onOrderPaidByBonuses={(orderId) => {
              setPaymentToast({ orderId, type: 'success' });
              setCart([]);
              setActivePage('orders');
            }}
            subscriptionCart={subscriptionCart}
            setSubscriptionCart={setSubscriptionCart}
            preferredCartTab={preferredCartTab}
            onSubscriptionInvoiceSuccess={(orderNumber) => {
              setPaymentToast({ orderId: orderNumber, type: 'success', isSubscription: true, isInvoice: true });
              setActivePage('orders');
            }}
          />
        );
      case 'orders':
        return <OrdersPage key={ordersKey} />;
      case 'profile':
        return <ProfilePage key={profileKey} goToOrders={() => setActivePage('orders')} />;
      case 'about':
        return <AboutPage />;
      default:
        return (
          <MenuPage
            addToCart={addToCart}
            addBouquetToCart={addBouquetToCart}
            cart={cart}
            updateQuantity={updateQuantity}
            updateBouquetQuantity={updateBouquetQuantity}
            onLoadingChange={setCatalogLoading}
            resetKey={catalogResetKey}
            catalogMode={catalogMode}
            onCatalogModeChange={(mode) => { setCatalogMode(mode); if (mode !== 'subscription') setSubSelection(null); }}
            onAddSubscriptionToCart={(item, deliveryCount) => {
              setSubscriptionCart({ catalog_item: item, delivery_count: deliveryCount });
              setSubSelection(null);
              setPreferredCartTab('subscription');
              setActivePage('cart');
            }}
            subscriptionCart={subscriptionCart}
            onSubSelectionChange={(item, count) => {
              if (item) {
                setSubSelection({ item, deliveryCount: count });
              } else {
                setSubSelection(null);
                setSubscriptionCart(null);
              }
            }}
          />
        );
    }
  };

  // Считаем общую сумму корзины (price может отсутствовать во время гидрации)
  const productsTotal = cart.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
  const subTotal = subscriptionCart ? subscriptionCart.catalog_item.price * subscriptionCart.delivery_count : 0;
  // Сумма текущего выбора на каталоге подписок (обновляется при каждом +/-)
  const subSelectionTotal = subSelection ? subSelection.item.price * subSelection.deliveryCount : 0;

  // Web-юзерам разрешён доступ: либо SMS OTP / Telegram Login через WebAuthModal,
  // либо гостевой заказ. Wall с «Открыть в Telegram/Max» больше не показываем.

  return (
    <div className="app">
      <Header
        showTabs={activePage === 'menu'}
        catalogMode={catalogMode}
        onCatalogModeChange={(mode) => { setCatalogMode(mode); if (mode !== 'subscription') setSubSelection(null); }}
      />
      {renderPage()}

      {/* Cookie-баннер (152-ФЗ) — рендерится только на web-платформе */}
      <CookieBanner />

      {/* Toast уведомление о привязке аккаунтов */}
      {linkToast && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '320px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {linkToast.type === 'link_success'
                ? <HeroIcon name="link" size={40} />
                : <HeroIcon name="alert" size={40} />}
            </div>
            <h2 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '20px' }}>
              {linkToast.type === 'link_success'
                ? 'Аккаунты объединены!'
                : 'Не удалось привязать'}
            </h2>
            <p style={{ margin: '0', color: '#666', fontSize: '14px', lineHeight: '1.4' }}>
              {linkToast.message}
            </p>
          </div>
        </div>
      )}

      {/* Разрешение боту писать — ступень открытия (только TG без Старта) */}
      <NotifyPermissionPopup
        isOpen={showWriteAccessPopup}
        stage="open"
        onAllow={() => { setShowWriteAccessPopup(false); requestWriteAccessNative(); }}
        onLater={() => setShowWriteAccessPopup(false)}
      />

      {/* Toast уведомление о статусе оплаты. Поднимаем выше центра ТОЛЬКО
          для попапов нативного инвойса на десктопных клиентах TG — там
          системный тост «Вы заплатили…» рисуется по центру поверх вебвью.
          Redirect-флоу (и все мобильные) — попап по центру, как всегда. */}
      {paymentToast && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: (paymentToast.viaInvoice && ['tdesktop', 'macos'].includes(getWebApp()?.platform)) ? 'flex-start' : 'center',
          paddingTop: (paymentToast.viaInvoice && ['tdesktop', 'macos'].includes(getWebApp()?.platform)) ? '7vh' : 0,
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '320px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <div style={{ marginBottom: '16px' }}>
              {paymentToast.type === 'success'
                ? <HeroIcon name="paid" size={56} />
                : <HeroIcon name="unpaid" size={56} />}
            </div>
            <h2 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '20px' }}>
              {paymentToast.type === 'success'
                ? (paymentToast.isCertificate
                    ? 'Вы успешно оплатили сертификат!'
                    : paymentToast.isSubscription
                      ? (paymentToast.isInvoice ? 'Подписка успешно оформлена!' : 'Подписка успешно оплачена!')
                      : 'Ваш заказ успешно оплачен!')
                : (paymentToast.isCertificate
                    ? 'Вы не оплатили ваш сертификат. Мы грустим ;('
                    : 'Вы не оплатили заказ. Мы грустим ;(')}
            </h2>
            <p style={{ margin: '0', color: '#666', fontSize: '16px' }}>
              {paymentToast.isCertificate
                ? 'Сертификат'
                : paymentToast.isSubscription ? 'Подписка' : 'Заказ'} №{paymentToast.orderId}
            </p>
            {paymentToast.isInvoice && (
              <p style={{ margin: '12px 0 0', color: '#888', fontSize: '13px', lineHeight: '1.4' }}>
                В ближайшее время с вами свяжется сотрудник поддержки
              </p>
            )}
          </div>
        </div>
      )}

      {/* Кнопка "Перейти в корзину" */}
      {activePage === 'menu' && !catalogLoading && (() => {
        // На каталоге подписок: показываем сумму текущего выбора, или сумму из корзины
        if (catalogMode === 'subscription') {
          if (subSelection) {
            return (
              <button className="floating-cart-btn" onClick={() => {
                trackEvent('cart_opened');
                setSubscriptionCart({ catalog_item: subSelection.item, delivery_count: subSelection.deliveryCount });
                setSubSelection(null);
                setPreferredCartTab('subscription');
                setActivePage('cart');
              }}>
                Перейти в корзину <span>{subSelectionTotal.toLocaleString('ru-RU')} ₽</span>
              </button>
            );
          }
          if (subscriptionCart) {
            return (
              <button className="floating-cart-btn" onClick={() => { trackEvent('cart_opened'); setPreferredCartTab('subscription'); setActivePage('cart'); }}>
                Перейти в корзину <span>{subTotal.toLocaleString('ru-RU')} ₽</span>
              </button>
            );
          }
          return null;
        }
        // На каталоге товаров
        if (cart.length > 0 && productsTotal > 0) {
          return (
            <button className="floating-cart-btn" onClick={() => { trackEvent('cart_opened'); setPreferredCartTab('products'); setActivePage('cart'); }}>
              Перейти в корзину <span>{productsTotal.toLocaleString('ru-RU')} ₽</span>
            </button>
          );
        }
        return null;
      })()}
      
      <Navigation activePage={activePage} setActivePage={setActivePage} cartCount={cart.filter(i => i.price != null).length + (subscriptionCart ? 1 : 0)} />
    </div>
  );
}

export default AppRouter;
